import { useTranslation } from 'react-i18next';
import { Store } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { BlockConceptsViewDto } from '@subscriptions/application/dtos';

interface BlockConceptsCardsProps {
  concepts: BlockConceptsViewDto;
}

/** The concepts breakdown on a phone: one card per charge, with the totals as plain rows. */
export const BlockConceptsCards = ({ concepts }: BlockConceptsCardsProps) => {
  const { t } = useTranslation('subscriptions');
  const { groups, conceptCount, totalLabel } = concepts;
  const [firstGroup] = groups;
  const isGrouped = firstGroup?.isGrouped ?? false;

  return (
    <div className="flex flex-col gap-2.5 font-sans">
      {groups.map((group) => (
        <div className="flex flex-col gap-2.5" key={group.storeUuid}>
          {group.isGrouped && (
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2.5">
              <Store className="h-3.5 w-3.5" />
              <span className="text-sm font-semibold">{group.storeName}</span>
            </div>
          )}

          {group.rows.map((row) => (
            <div className="rounded-lg border border-border p-3.5" key={row.key}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {row.showMultiplier ? `${row.name} × ${row.quantityLabel}` : row.name}
                  </div>
                  {row.description && <div className="mt-0.5 text-xs text-muted-foreground">{row.description}</div>}
                </div>
                <div className="whitespace-nowrap text-sm font-semibold tabular-nums">{row.amountLabel}</div>
              </div>
              {row.periodLabel && <div className="mt-1.5 text-xs">{row.periodLabel}</div>}
            </div>
          ))}

          {group.isGrouped && (
            <>
              <div className="flex justify-between gap-3 px-0.5 text-sm">
                <span>{t('blockDetail.summary.concepts.discount')}</span>
                <span
                  className={cn('font-medium tabular-nums', {
                    'text-success': group.hasDiscount,
                    'text-muted-foreground': !group.hasDiscount,
                  })}
                >
                  {group.discountLabel}
                </span>
              </div>
              <div className="flex justify-between gap-3 px-0.5 text-sm">
                <span className="text-muted-foreground">
                  {t('blockDetail.summary.concepts.subtotal', { store: group.storeName })}
                </span>
                <span className="font-semibold tabular-nums">{group.subtotalLabel}</span>
              </div>
            </>
          )}
        </div>
      ))}

      {!isGrouped && firstGroup && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3.5 text-sm">
          <div className="min-w-0">
            <div className="font-semibold">{t('blockDetail.summary.concepts.globalDiscount')}</div>
            {!firstGroup.hasDiscount && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t('blockDetail.summary.concepts.noDiscounts')}
              </div>
            )}
          </div>
          <div
            className={cn('whitespace-nowrap font-semibold tabular-nums', {
              'text-success': firstGroup.hasDiscount,
              'text-muted-foreground': !firstGroup.hasDiscount,
            })}
          >
            {firstGroup.discountLabel}
          </div>
        </div>
      )}

      <div className="rounded-lg bg-muted p-3.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-base font-bold">{t('blockDetail.summary.concepts.total')}</span>
          <span className="text-base font-bold tabular-nums">{totalLabel}</span>
        </div>
        <div className="mt-1 flex justify-between gap-3 text-xs text-muted-foreground">
          <span>
            {conceptCount === 1
              ? t('blockDetail.summary.concepts.conceptCountOne')
              : t('blockDetail.summary.concepts.conceptCountOther', { count: conceptCount })}
          </span>
          <span>{t('blockDetail.summary.taxIncluded')}</span>
        </div>
      </div>
    </div>
  );
};
